import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import CustomSpinner from '../../../loader/CustomSpinner';
import { setSelectedView } from '../../../redux/routeProfileSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { RouteService } from '../../../services/route/routeService';
import { RouteApiResponse } from '../../../type/entities/route-type';

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.routes.profile.info,
        permission: {
            group: 'route',
            action: 'get',
        },
    },
    {
        label: 'Sedes',
        path: menuRoutes.routes.profile.headquarters,
        permission: {
            group: 'headquarter',
            action: 'list',
        },
    }
];

function RouteProfileLayout({ isLoading }: { isLoading?: boolean }) {

    // HOOKS
    const [changingStatus, setChangingStatus] = useState<string[]>([]);

    const { selectedView } = useSelector((state: RootState) => state.routeProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();
    const service = new RouteService();

    // DATA FETCHING

    //--------------------------------------------------------------------------------------------------------------
    /**
     *  @EN FETCHES ROUTE DATA BY ID.
     *  @ES OBTIENE LOS DATOS DE UNA RUTA POR SU ID.
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loadingRoute, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getRouteById(id);
        return response.getResponseData() as RouteApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    const pageHeader = {
        title: `Perfil de ${data?.name || 'Ruta'}`,
        breadcrumb: [
            { name: 'Rutas', href: menuRoutes.routes.path },
            { name: 'Perfil de Ruta' },
        ],
    };

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL CAMBIO DE ESTADO DE LA RUTA
     * @EN HANDLE THE CHANGE OF ROUTE STATUS
     */
    //--------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado la ruta" : "Se ha activado la ruta");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.routes.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {data && (
                        <>
                            <Tooltip
                                size="sm"
                                content={data.status ? 'Desactivar' : 'Activar'}
                                placement="top"
                                color="invert"
                            >
                                <Switch
                                    id={id}
                                    label={data.status ? 'Activada' : 'Desactivada'}
                                    checked={data.status}
                                    disabled={changingStatus.includes(data.id)}
                                    onChange={() => toggleStatus(data.id, data.status)}
                                    switchKnobClassName='bg-secondary'
                                />
                            </Tooltip>
                        </>
                    )}
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <div className='m-auto h-full'><CustomSpinner /></div> : <Outlet />}
        </>
    );
}

export default RouteProfileLayout;